const DARK_THEME_CLASSES = ['dark', 'dark-mode', 'theme-dark', 'is-dark'];

/**
 * @param {string | null | undefined} value
 * @returns {boolean | null}
 */
function parseExplicitThemeValue(value) {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    const normalized = String(value).trim().toLowerCase();

    if (normalized === 'dark') {
        return true;
    }

    if (normalized === 'light') {
        return false;
    }

    return null;
}

/**
 * @param {Element} root
 * @returns {boolean | null}
 */
function readExplicitThemeFromElement(root) {
    for (const attribute of ['data-theme', 'data-bs-theme', 'data-color-mode']) {
        const explicit = parseExplicitThemeValue(root.getAttribute(attribute));

        if (explicit !== null) {
            return explicit;
        }
    }

    for (const className of DARK_THEME_CLASSES) {
        if (root.classList.contains(className)) {
            return true;
        }
    }

    return null;
}

/**
 * @param {Element} element
 * @returns {boolean | null}
 */
function readColorSchemeFromElement(element) {
    try {
        const scheme = getComputedStyle(element).colorScheme;

        if (! scheme || scheme === 'normal') {
            return null;
        }

        if (scheme.includes('dark')) {
            return true;
        }

        if (scheme === 'light') {
            return false;
        }
    } catch {
        // ignore
    }

    return null;
}

/**
 * Host oldal sötét/világos témájának felismerése (tenant-agnosztikus mechanizmus).
 */
export function detectHostDarkMode() {
    const root = document.documentElement;

    const explicitOnRoot = readExplicitThemeFromElement(root);

    if (explicitOnRoot !== null) {
        return explicitOnRoot;
    }

    const schemeOnRoot = readColorSchemeFromElement(root);

    if (schemeOnRoot !== null) {
        return schemeOnRoot;
    }

    if (document.body) {
        const explicitOnBody = readExplicitThemeFromElement(document.body);

        if (explicitOnBody !== null) {
            return explicitOnBody;
        }

        const schemeOnBody = readColorSchemeFromElement(document.body);

        if (schemeOnBody !== null) {
            return schemeOnBody;
        }
    }

    try {
        return window.matchMedia('(prefers-color-scheme: dark)').matches;
    } catch {
        return false;
    }
}

/**
 * @param {(isDark: boolean) => void} onChange
 * @returns {() => void}
 */
export function subscribeHostTheme(onChange) {
    let current = detectHostDarkMode();

    const notify = () => {
        const next = detectHostDarkMode();

        if (next === current) {
            return;
        }

        current = next;
        onChange(next);
    };

    const observer = new MutationObserver(notify);

    observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class', 'data-theme', 'data-bs-theme', 'data-color-mode', 'style'],
    });

    if (document.body) {
        observer.observe(document.body, {
            attributes: true,
            attributeFilter: ['class', 'data-theme', 'data-bs-theme', 'data-color-mode', 'style'],
        });
    }

    let mediaQuery = null;

    try {
        mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        mediaQuery.addEventListener('change', notify);
    } catch {
        // ignore
    }

    return () => {
        observer.disconnect();

        if (mediaQuery) {
            mediaQuery.removeEventListener('change', notify);
        }
    };
}
